import { useMemo, useRef, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Checkbox } from './FieldComponents';
import Modal from './Modal';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';
import {
    BILLING_ENTRY_EXPORT_COLUMNS,
    DEFAULT_BILLING_ENTRY_EXPORT_COLUMNS,
    billingEntriesExportFilenameFromResponse,
    buildBillingEntriesExportFilename,
    buildBillingEntriesExportRequest,
    createBillingEntryExportFilters,
    downloadBillingEntriesExport,
    filterBillingEntriesForExport,
    validateBillingEntryExportFilters,
} from './billingEntryExport';

const apiOrigin = () => {
    const configured = String(process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');
    if (configured) return configured;
    if (typeof window === 'undefined') return '';
    const { protocol, hostname, origin } = window.location;
    if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('api.')) {
        return origin;
    }
    const labels = hostname.split('.').filter(Boolean);
    const applicationRoot = labels.length > 2 ? labels.slice(-2).join('.') : hostname;
    return `${protocol}//api.${applicationRoot}`;
};

const EXPORT_ENDPOINT = `${apiOrigin()}/billing_entries_export.php`;

const responseError = async (response) => {
    const text = await response.text();
    try {
        const data = text ? JSON.parse(text) : null;
        return data?.message || data?.error || 'The billing entries could not be exported.';
    } catch (error) {
        return 'CaseDB returned an invalid response while exporting billing entries.';
    }
};

const uniqueValues = (entries, field) => Array.from(new Set(
    (Array.isArray(entries) ? entries : [])
        .map((entry) => entry?.[field])
        .filter((value) => value !== null && value !== undefined && String(value) !== '')
        .map(String)
)).sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));

const BillingEntriesExportModal = ({ caseId, entries = [], users = [], rateEntries = [], onClose, onExported }) => {
    const [selectedColumns, setSelectedColumns] = useState(() => [...DEFAULT_BILLING_ENTRY_EXPORT_COLUMNS]);
    const [filters, setFilters] = useState(createBillingEntryExportFilters);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const exportInFlightRef = useRef(false);

    const previewEntries = useMemo(() => {
        const userByRate = new Map(
            (Array.isArray(rateEntries) ? rateEntries : [])
                .map((rate) => [String(rate?.id ?? ''), rate?.user_id])
                .filter(([id, userId]) => id !== '' && Number(userId) > 0)
        );
        return (Array.isArray(entries) ? entries : []).map((entry) => ({
            ...entry,
            effective_user_id: Number(entry?.user_id) > 0
                ? entry.user_id
                : userByRate.get(String(entry?.billing_rate_id ?? '')) ?? entry?.user_id,
        }));
    }, [entries, rateEntries]);
    const filteredEntries = useMemo(
        () => filterBillingEntriesForExport(previewEntries, filters),
        [previewEntries, filters]
    );
    const statuses = useMemo(() => uniqueValues(entries, 'status'), [entries]);
    const filterError = validateBillingEntryExportFilters(filters);

    const updateFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));
    const toggleColumn = (key) => setSelectedColumns((current) => (
        current.includes(key)
            ? current.filter((column) => column !== key)
            : [...current, key]
    ));

    const exportEntries = async () => {
        if (exportInFlightRef.current) return;

        let payload;
        try {
            payload = buildBillingEntriesExportRequest({
                caseId,
                columnKeys: selectedColumns,
                filters,
            });
        } catch (validationError) {
            setError(validationError?.message || 'Review the export settings and try again.');
            return;
        }

        exportInFlightRef.current = true;
        setExporting(true);
        setError('');
        try {
            await ensureCaseDbCsrfToken();
            const response = await fetch(EXPORT_ENDPOINT, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(await responseError(response));

            const blob = await response.blob();
            const fallback = buildBillingEntriesExportFilename(caseId);
            const filename = billingEntriesExportFilenameFromResponse(response, fallback);
            downloadBillingEntriesExport(blob, filename);
            await onExported?.({ filename, rowCount: filteredEntries.length });
        } catch (exportError) {
            setError(exportError?.message || 'The billing entries could not be exported.');
        } finally {
            exportInFlightRef.current = false;
            setExporting(false);
        }
    };

    const canExport = !exporting && selectedColumns.length > 0 && !filterError;

    return (
        <Modal
            title='Export Billing Entries'
            instructions='Choose the columns and entries to include in the CSV file.'
            wide
            onClose={exporting ? null : onClose}
            footer={(
                <div className='modal-footer-actions'>
                    <button className='action alt' type='button' onClick={onClose} disabled={exporting}>Cancel</button>
                    <button
                        className='action'
                        type='button'
                        onClick={exportEntries}
                        disabled={!canExport}
                        aria-busy={exporting}
                    >
                        {exporting
                            ? <><Loader2 size={16} className='spin' /> Exporting…</>
                            : <><Download size={16} /> Export CSV</>}
                    </button>
                </div>
            )}
        >
            <div className='modal-content-wrapper billing-entries-export-modal'>
                <section className='billing-entries-export-section'>
                    <div className='billing-entries-export-heading'>
                        <div>
                            <strong>Columns</strong>
                            <span className='small-text'>{selectedColumns.length} selected</span>
                        </div>
                        <div className='billing-entries-export-shortcuts'>
                            <button className='text-button' type='button' onClick={() => setSelectedColumns([...DEFAULT_BILLING_ENTRY_EXPORT_COLUMNS])} disabled={exporting}>Defaults</button>
                            <button className='text-button' type='button' onClick={() => setSelectedColumns(BILLING_ENTRY_EXPORT_COLUMNS.map(({ key }) => key))} disabled={exporting}>Select all</button>
                            <button className='text-button' type='button' onClick={() => setSelectedColumns([])} disabled={exporting}>Clear</button>
                        </div>
                    </div>
                    <div className='billing-entries-export-columns'>
                        {BILLING_ENTRY_EXPORT_COLUMNS.map((column) => (
                            <Checkbox
                                key={column.key}
                                value={selectedColumns.includes(column.key)}
                                onChange={() => !exporting && toggleColumn(column.key)}
                                label={column.label}
                            />
                        ))}
                    </div>
                </section>

                <section className='billing-entries-export-section'>
                    <div className='billing-entries-export-heading'>
                        <div>
                            <strong>Filters</strong>
                            <span className='small-text'>Leave a filter blank to include all values.</span>
                        </div>
                        <button className='text-button' type='button' onClick={() => setFilters(createBillingEntryExportFilters())} disabled={exporting}>Clear filters</button>
                    </div>

                    <div className='billing-entries-export-filters'>
                        <label>
                            <span>Entry date from</span>
                            <input type='date' value={filters.entryDateFrom} onChange={(event) => updateFilter('entryDateFrom', event.target.value)} disabled={exporting} />
                        </label>
                        <label>
                            <span>Entry date to</span>
                            <input type='date' value={filters.entryDateTo} onChange={(event) => updateFilter('entryDateTo', event.target.value)} disabled={exporting} />
                        </label>
                        <label>
                            <span>Created date from</span>
                            <input type='date' value={filters.createdDateFrom} onChange={(event) => updateFilter('createdDateFrom', event.target.value)} disabled={exporting} />
                        </label>
                        <label>
                            <span>Created date to</span>
                            <input type='date' value={filters.createdDateTo} onChange={(event) => updateFilter('createdDateTo', event.target.value)} disabled={exporting} />
                        </label>
                        <label>
                            <span>Team member</span>
                            <select value={filters.userId} onChange={(event) => updateFilter('userId', event.target.value)} disabled={exporting}>
                                <option value=''>All team members</option>
                                {users.map((user) => <option key={user.id} value={user.id}>{user.name || user.user || user.email || `User ${user.id}`}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>Entry type</span>
                            <select value={filters.entryKind} onChange={(event) => updateFilter('entryKind', event.target.value)} disabled={exporting}>
                                <option value=''>All entry types</option>
                                <option value='time'>Time</option>
                                <option value='expense'>Expense</option>
                                <option value='flat_fee'>Flat fee</option>
                            </select>
                        </label>
                        <label>
                            <span>Invoice status</span>
                            <select value={filters.invoiceState} onChange={(event) => updateFilter('invoiceState', event.target.value)} disabled={exporting}>
                                <option value=''>All entries</option>
                                <option value='unbilled'>Not invoiced</option>
                                <option value='invoiced'>Invoiced</option>
                            </select>
                        </label>
                        <label>
                            <span>Entry status</span>
                            <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)} disabled={exporting}>
                                <option value=''>All statuses</option>
                                {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>Minimum time / quantity</span>
                            <input type='number' step='any' value={filters.minQuantity} onChange={(event) => updateFilter('minQuantity', event.target.value)} disabled={exporting} />
                        </label>
                        <label>
                            <span>Maximum time / quantity</span>
                            <input type='number' step='any' value={filters.maxQuantity} onChange={(event) => updateFilter('maxQuantity', event.target.value)} disabled={exporting} />
                        </label>
                        <label className='billing-entries-export-filter-wide'>
                            <span>Description contains</span>
                            <input type='text' value={filters.description} onChange={(event) => updateFilter('description', event.target.value)} disabled={exporting} />
                        </label>
                    </div>
                </section>

                {(error || filterError) && <div className='billing-time-import-error' role='alert'>{error || filterError}</div>}
                {selectedColumns.length === 0 && <div className='billing-time-import-error' role='alert'>Select at least one column to export.</div>}
                <div className='billing-entries-export-summary' aria-live='polite'>
                    <strong>{filteredEntries.length}</strong>
                    <span>{filteredEntries.length === 1 ? 'entry matches' : 'entries match'} the current filters</span>
                    <span className='small-text'>out of {entries.length} currently loaded for this case</span>
                </div>
            </div>
        </Modal>
    );
};

export default BillingEntriesExportModal;
